/**
 * useBlockAuthoringShell — Track 3 Phase 6
 *
 * Owns the Block Authoring palette feature extracted from `App.tsx`
 * as part of the Track 3 App.tsx decomposition (see
 * `docs/plans/2026-05-08_track3_app_decomposition.md`). Bundles two
 * related concerns that share the BEDIT-only mounting predicate and
 * the BAUTHORPALETTE/BAUTHORPALETTECLOSE/BPARAMETER/BACTION/BVSTATE
 * command-line entry points:
 *
 *   1. The DYN-1.7 palette **visibility flag** (`paletteVisible`) and
 *      the open-request signal (`paletteOpenRequest`) that the
 *      command-line router uses to switch the palette to the right
 *      tab when the user types BPARAMETER / BACTION / BVSTATE /
 *      BCPARAMETER.
 *
 *   2. The Phase K.1 **floating shell state**
 *      (`shellState: BlockAuthoringPaletteShellState`) — open flag,
 *      drag position, active tab, dock side. Persisted to
 *      `localStorage` via `persistBlockAuthoringPaletteState` on
 *      every change.
 *
 * Responsibilities:
 *   - Lazy-hydrate the shell state from localStorage at mount via
 *     `readPersistedBlockAuthoringPaletteState`, falling back to
 *     `DEFAULT_BLOCK_AUTHORING_PALETTE_STATE`.
 *   - Persist the shell state on every change (small payload, fires
 *     only on user interactions: open / close / tab / move / dock).
 *   - Expose a tight imperative surface for the command-line router
 *     and the ribbon: `togglePalette()`, `closePalette()`,
 *     `requestOpen(kind)`, `consumeOpenRequest()`, `openShellTab(tab)`,
 *     `closeShell()`, `setShellTab(tab)`, `setShellPosition(pos)`.
 *
 * Returned shape:
 *   {
 *     palette:      { visible, openRequest, requestOpen,
 *                     consumeOpenRequest, toggle, close, setVisible },
 *     shell:        { state, setTab, setPosition, openOnTab, close },
 *   }
 *
 * The orchestrator (App.tsx) drives all three render-prop blocks:
 *   - DYN-1.7 palette mount predicate is `palette.visible &&
 *     activeSpace.kind === "block-edit"`. The hook does **not** know
 *     about the active space — that gate stays in App.tsx because it
 *     also depends on `geometryDocument.spaces` (out of scope here).
 *   - Phase K.1 shell mount predicate is `inBlockEdit` only — the
 *     `shell.state.open` flag is independent of `palette.visible`
 *     (matches AutoCAD which has both panels living side-by-side).
 *
 * AutoCAD parity:
 *   AutoCAD's BAUTHORPALETTE toggles a single docked palette window;
 *   our editor has a DYN-1.7 dialog-driven palette AND a Phase K.1
 *   floating shell, which together cover the AutoCAD parameter /
 *   action / visibility / parameter-sets / constraint surface. Both
 *   are gated on BEDIT — matching AutoCAD's behaviour where the
 *   palette is only available inside the Block Editor.
 */

import { useCallback, useEffect, useState } from "react";

import {
  DEFAULT_BLOCK_AUTHORING_PALETTE_STATE,
  persistBlockAuthoringPaletteState,
  readPersistedBlockAuthoringPaletteState,
  type BlockAuthoringPaletteShellState,
} from "../components/BlockAuthoringPaletteShell";
import type { BlockAuthoringPaletteTab } from "../components/BlockAuthoringPaletteShellTabs";

/**
 * Open-request signal carried from the command-line router into the
 * DYN-1.7 palette. The palette consumes the signal in an effect,
 * switches to the matching tab, and calls `consumeOpenRequest()` so
 * the parent clears the value (avoids re-triggering on the next
 * render).
 */
export type BlockAuthoringPaletteOpenRequest =
  | "parameter"
  | "action"
  | "visibility"
  | "constraint"
  | null;

export interface UseBlockAuthoringShellResult {
  palette: {
    /** Whether the DYN-1.7 palette should mount inside BEDIT. */
    visible: boolean;
    /** Latest open request from the command-line router (or null). */
    openRequest: BlockAuthoringPaletteOpenRequest;
    /** Set the open request — used by BPARAMETER / BACTION / BVSTATE / BCPARAMETER. */
    requestOpen: (kind: Exclude<BlockAuthoringPaletteOpenRequest, null>) => void;
    /** Clear the open request. Called by the palette after acting on it. */
    consumeOpenRequest: () => void;
    /** Toggle visibility — used by BAUTHORPALETTE. */
    toggle: () => void;
    /** Force visibility off — used by BAUTHORPALETTECLOSE. */
    close: () => void;
    /** Imperative setter — used by ribbon buttons that need to force-show. */
    setVisible: (next: boolean) => void;
  };
  shell: {
    /** Current floating-palette state (open / position / tab / docked). */
    state: BlockAuthoringPaletteShellState;
    /** Update the active tab. */
    setTab: (tab: BlockAuthoringPaletteTab) => void;
    /** Update the floating position (drag-end callback). */
    setPosition: (next: { x: number; y: number }) => void;
    /** Open the shell on a specific tab — used by ribbon buttons. */
    openOnTab: (tab: BlockAuthoringPaletteTab) => void;
    /** Close the shell — used by the header ✕ button. */
    close: () => void;
  };
}

export function useBlockAuthoringShell(): UseBlockAuthoringShellResult {
  // ── DYN-1.11 visibility flag + open-request ──
  // BAUTHORPALETTE toggles `visible`; BAUTHORPALETTECLOSE forces it
  // off. BPARAMETER / BACTION / BVSTATE / BCPARAMETER force it on AND
  // stamp `openRequest`, which the DYN-1.7 palette consumes in an
  // effect.
  const [paletteVisible, setPaletteVisible] = useState(true);
  const [paletteOpenRequest, setPaletteOpenRequest] =
    useState<BlockAuthoringPaletteOpenRequest>(null);

  // ── Phase K.1 floating shell state ──
  // Hydrated lazily so we read localStorage exactly once during
  // mount. Persisted on every change in the effect below.
  const [shellState, setShellState] =
    useState<BlockAuthoringPaletteShellState>(() => {
      const persisted = readPersistedBlockAuthoringPaletteState();
      return persisted ?? DEFAULT_BLOCK_AUTHORING_PALETTE_STATE;
    });

  useEffect(() => {
    persistBlockAuthoringPaletteState(shellState);
  }, [shellState]);

  // ── Palette imperative surface ──
  const togglePalette = useCallback(() => {
    setPaletteVisible((v) => !v);
  }, []);

  const closePalette = useCallback(() => {
    setPaletteVisible(false);
  }, []);

  const requestOpen = useCallback(
    (kind: Exclude<BlockAuthoringPaletteOpenRequest, null>) => {
      setPaletteVisible(true);
      setPaletteOpenRequest(kind);
    },
    [],
  );

  const consumeOpenRequest = useCallback(() => {
    setPaletteOpenRequest(null);
  }, []);

  // ── Shell imperative surface ──
  const setShellTab = useCallback((tab: BlockAuthoringPaletteTab) => {
    setShellState((prev) => ({ ...prev, activeTab: tab }));
  }, []);

  const setShellPosition = useCallback(
    (next: { x: number; y: number }) => {
      setShellState((prev) => ({ ...prev, position: next }));
    },
    [],
  );

  const openShellOnTab = useCallback((tab: BlockAuthoringPaletteTab) => {
    setShellState((prev) => ({ ...prev, open: true, activeTab: tab }));
  }, []);

  const closeShell = useCallback(() => {
    setShellState((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    palette: {
      visible: paletteVisible,
      openRequest: paletteOpenRequest,
      requestOpen,
      consumeOpenRequest,
      toggle: togglePalette,
      close: closePalette,
      setVisible: setPaletteVisible,
    },
    shell: {
      state: shellState,
      setTab: setShellTab,
      setPosition: setShellPosition,
      openOnTab: openShellOnTab,
      close: closeShell,
    },
  };
}
